
import { useState } from "react";
import { DashboardFeature } from "../features/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BartenderSchedulePage } from "../bartender/BartenderSchedulePage";
import { BartenderCertificationsPage } from "../bartender/BartenderCertificationsPage";
import { BartenderReportsPage } from "../bartender/BartenderReportsPage";
import { SecuritySchedulePage } from "../security/SecuritySchedulePage";
import { BookingsPage } from "../bookings/BookingsPage";
import { EventsPage } from "../events/EventsPage";

interface FeatureCardsProps {
  features: DashboardFeature[];
}

export const FeatureCards = ({ features }: FeatureCardsProps) => {
  const [selectedFeature, setSelectedFeature] = useState<string>(features.length > 0 ? features[0].href : "");

  if (features.length === 0) {
    return (
      <div className="glass-morphism p-8 rounded-lg text-center text-muted-foreground">
        <p>No features available for this role</p>
      </div>
    );
  }

  // Map routes to components
  const getComponentForRoute = (route: string) => {
    switch (route) {
      // Bartender routes
      case "/dashboard/staff/schedule":
        return <BartenderSchedulePage />;
      case "/dashboard/staff/reports":
        return <BartenderReportsPage />;
      case "/dashboard/staff/certifications":
        return <BartenderCertificationsPage />;
      
      // Security routes
      case "/dashboard/security/schedule":
        return <SecuritySchedulePage />;
      
      // DJ/Performer routes
      case "/dashboard/dj/bookings":
      case "/dashboard/performer/bookings":
        return <BookingsPage />;
      
      // Promoter routes
      case "/dashboard/events/create":
        return <EventsPage />;
      
      // For other routes, show a placeholder
      default:
        return (
          <div className="p-8 text-center text-muted-foreground glass-morphism rounded-lg flex flex-col items-center justify-center min-h-[200px]">
            <p className="mb-4">Feature content will be displayed here</p>
            <p className="text-sm text-purple-400">{route}</p>
            <div className="mt-4 w-16 h-16 rounded-full bg-purple-500/10 animate-pulse"></div>
          </div>
        );
    }
  };

  return (
    <div className="w-full space-y-6">
      <Tabs
        value={selectedFeature}
        onValueChange={setSelectedFeature}
        className="w-full"
      >
        <TabsList className="w-full justify-start flex-wrap h-auto gap-2 bg-transparent">
          {features.map((feature, index) => (
            <TabsTrigger
              key={index}
              value={feature.href}
              className="flex gap-2 items-center px-4 py-2 backdrop-blur-sm data-[state=active]:bg-gradient-to-r from-purple-600/80 to-pink-600/80 data-[state=active]:shadow-[0_0_15px_rgba(139,92,246,0.3)] data-[state=inactive]:bg-black/30 transition-all duration-300"
            >
              <feature.icon className="h-4 w-4" />
              {feature.title}
            </TabsTrigger>
          ))}
        </TabsList>
        
        {features.map((feature, index) => (
          <TabsContent key={index} value={feature.href} className="mt-6 animate-fade-in">
            {getComponentForRoute(feature.href)}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
